import type { IFutureFlag } from '../types';

import { EMAIL_GEMCOMMERCE, EMAIL_SUPPORT } from '~/modules/integration';
import { checkFutureFlag, checkFutureFlagByStorage } from '../helpers';
import { META_KEY_FEATURE_FLAG } from '~/constants/shopMeta';
import { findAndParseShopMeta, isBrowser } from '~/utils';
import { useShopInfo } from '../hooks/useShopInfo';
import { FutureFlag } from '../types';
import { useMemo } from 'react';

export interface IFeatureFlagCheckerResult {
  isGemUser: boolean;
  isEmailSupport: boolean;
  isEnabledLanguageSetting: boolean;
  isEnabledMakeWinnerButton: boolean;
  isEnabledPriceTesting: boolean;
  isEnabledHeatmapBeta: boolean;
  isEnabledHeatmapV1: boolean;
  isEnabledHeatmapV2: boolean;
  isEnabledHeatmapV3: boolean;
  isEnabledFilterByShopifyPageId: boolean;
  isEnabledDevMode: boolean;
  isEnabledPricingCoupon: boolean;
  isEnabledTemplatePageTypeLoading: boolean;
  isEnabledAfterMakeWinner: boolean;
  isEnabledHeatmapMockData: boolean;
}
export const useFeatureFlagChecker = (): IFeatureFlagCheckerResult => {
  const { shopInfo, shopFuture, shopMeta } = useShopInfo();

  const shopMetaFlags = useMemo(
    () =>
      shopMeta
        ? findAndParseShopMeta<Partial<IFutureFlag>>(shopMeta, META_KEY_FEATURE_FLAG)
        : undefined,
    [shopMeta],
  );

  const isEmailSupport = useMemo(() => {
    const email = shopInfo?.email;
    return !!email && EMAIL_SUPPORT.includes(email);
  }, [shopInfo?.email]);

  const isGemUser = useMemo(() => {
    const email = shopInfo?.email;
    return !!email && !!EMAIL_GEMCOMMERCE.some((item) => email.includes(item));
  }, [shopInfo?.email]);

  const isEnabledMakeWinnerButton = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.MAKE_WINNER_BUTTON, shopFuture, shopMetaFlags)) return true;

    return checkFutureFlagByStorage(FutureFlag.MAKE_WINNER_BUTTON);
  }, [shopFuture, shopMetaFlags]);

  const isEnabledPriceTesting = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.PRICE_TESTING, shopFuture, shopMetaFlags)) return true;

    return checkFutureFlagByStorage(FutureFlag.PRICE_TESTING);
  }, [shopFuture, shopMetaFlags]);

  const isEnabledLanguageSetting = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.LANGUAGE_SETTING, shopFuture, shopMetaFlags)) return true;

    return checkFutureFlagByStorage(FutureFlag.LANGUAGE_SETTING);
  }, [shopFuture, shopMetaFlags]);

  const isEnabledFilterByShopifyPageId = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.FILTER_BY_SHOPIFY_PAGE_ID, shopFuture, shopMetaFlags))
      return true;

    return checkFutureFlagByStorage(FutureFlag.FILTER_BY_SHOPIFY_PAGE_ID);
  }, [shopFuture, shopMetaFlags]);

  const isEnabledDevMode = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.DEV_MODE, shopFuture, shopMetaFlags)) return true;

    return checkFutureFlagByStorage(FutureFlag.DEV_MODE);
  }, [shopFuture, shopMetaFlags]);

  const isEnabledPricingCoupon = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.PRICING_COUPON, shopFuture, shopMetaFlags)) return true;

    return checkFutureFlagByStorage(FutureFlag.PRICING_COUPON);
  }, [shopFuture, shopMetaFlags]);

  const isEnabledTemplatePageTypeLoading = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.HOTFIX_TEMPLATE_LOADING, shopFuture, shopMetaFlags)) return true;

    return checkFutureFlagByStorage(FutureFlag.HOTFIX_TEMPLATE_LOADING);
  }, [shopFuture, shopMetaFlags]);

  const isEnabledAfterMakeWinner = useMemo(() => {
    if (!isBrowser()) return false;

    const isEnabledByShopFuture = checkFutureFlag(FutureFlag.AFTER_MAKE_WINNER, shopFuture);
    if (isEnabledByShopFuture) return true;

    return checkFutureFlagByStorage(FutureFlag.AFTER_MAKE_WINNER);
  }, [shopFuture]);

  const isEnabledHeatmapMockData = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.HEATMAP_MOCK_DATA, shopFuture, shopMetaFlags)) return true;

    return checkFutureFlagByStorage(FutureFlag.HEATMAP_MOCK_DATA);
  }, [shopFuture, shopMetaFlags]);

  const isEnabledHeatmapBeta = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.HEATMAP_BETA, shopFuture, shopMetaFlags)) return true;

    return checkFutureFlagByStorage(FutureFlag.HEATMAP_BETA);
  }, [shopFuture, shopMetaFlags]);

  const isEnabledHeatmapV1 = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.HEATMAP_V1, shopFuture, shopMetaFlags)) return true;

    return checkFutureFlagByStorage(FutureFlag.HEATMAP_V1);
  }, [shopFuture, shopMetaFlags]);

  const isEnabledHeatmapV2 = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.HEATMAP_V2, shopFuture, shopMetaFlags)) return true;

    return checkFutureFlagByStorage(FutureFlag.HEATMAP_V2);
  }, [shopFuture, shopMetaFlags]);

  const isEnabledHeatmapV3 = useMemo(() => {
    if (!isBrowser()) return false;

    if (checkFutureFlag(FutureFlag.HEATMAP_V3, shopFuture, shopMetaFlags)) return true;

    return checkFutureFlagByStorage(FutureFlag.HEATMAP_V3);
  }, [shopFuture, shopMetaFlags]);

  return {
    isGemUser,
    isEmailSupport,
    isEnabledLanguageSetting,
    isEnabledPriceTesting,
    isEnabledFilterByShopifyPageId,
    isEnabledDevMode,
    isEnabledPricingCoupon,
    isEnabledTemplatePageTypeLoading,
    isEnabledMakeWinnerButton,
    isEnabledAfterMakeWinner,
    isEnabledHeatmapBeta,
    isEnabledHeatmapV1,
    isEnabledHeatmapV2,
    isEnabledHeatmapV3,
    isEnabledHeatmapMockData,
  };
};
